import { Clock, ChevronDown } from "lucide-react";
import { useState } from "react";

const dayLabels = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

type OldDayHours = { open: string; close: string; enabled: boolean };
type NewDayHours = { enabled: boolean; ranges: Array<{ start: string; end: string }> };
type DayHours = OldDayHours | NewDayHours;

interface ClosedStoreCardProps {
  businessHours: Record<string, DayHours> | null;
  type: "pedido" | "agendamento";
  // true = loja NÃO aceita pedido fora do horário (toggle desligado) — o card
  // vira aviso de bloqueio em vez de "pode prosseguir".
  blocked?: boolean;
}

function formatDayHours(h: DayHours): string {
  if ("ranges" in h && Array.isArray(h.ranges) && h.ranges.length > 0) {
    return h.ranges.map(r => `${r.start} - ${r.end}`).join(", ");
  }
  if ("open" in h && h.open && h.close) {
    return `${h.open} - ${h.close}`;
  }
  return "";
}

export function ClosedStoreCard({ businessHours, type, blocked }: ClosedStoreCardProps) {
  const [showHours, setShowHours] = useState(false);

  const activeDays = businessHours
    ? Object.entries(businessHours)
        .filter(([, h]) => h.enabled)
        .sort(([a], [b]) => ((Number(a) + 6) % 7) - ((Number(b) + 6) % 7))
        .map(([key, h]) => ({
          label: dayLabels[Number(key)] || key,
          hours: formatDayHours(h),
        }))
        .filter(d => d.hours !== "")
    : [];

  return (
    <div className="rounded-xl border border-orange-200 bg-orange-50 p-3 space-y-2 dark:bg-orange-950/20 dark:border-orange-800">
      <div className="flex items-start gap-2">
        <Clock className="w-4 h-4 text-orange-600 dark:text-orange-400 shrink-0 mt-0.5" />
        <p className="text-xs text-orange-800 dark:text-orange-300 leading-relaxed">
          {blocked
            ? `A loja está fechada no momento e não aceita ${type === "pedido" ? "pedidos" : "agendamentos"} fora do horário de funcionamento. Volte num dos horários abaixo.`
            : `A loja está fechada no momento. Você pode prosseguir normalmente com seu ${type}. Daremos andamento no próximo horário disponível.`}
        </p>
      </div>
      {activeDays.length > 0 && (
        <div className="pl-6">
          <button
            type="button"
            onClick={() => setShowHours(v => !v)}
            className="flex items-center gap-1 text-xs font-semibold text-orange-700 dark:text-orange-400 hover:text-orange-900 dark:hover:text-orange-300"
            aria-expanded={showHours}
          >
            Ver horários de funcionamento
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showHours ? "rotate-180" : ""}`} />
          </button>
          {showHours && (
            <div className="mt-2 space-y-0.5">
              {activeDays.map((d) => (
                <p key={d.label} className="text-xs text-orange-700 dark:text-orange-400">
                  {d.label}: {d.hours}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
